import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, DownloadButton, ResultPreview } from "@/components/ToolLayout";
import { BatchRunner } from "@/components/BatchRunner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { AudioProcessingProgress, showAudioError, FileSizeNotice, useAudioPipelineStep, NO_AUDIO_TRACK_MESSAGE } from "./uiHelpers";
import { extractAudioFromVideo, NoAudioTrackError, formatMinSecTenth as formatMinSec } from "./operations";
import { videoHasAudio } from "./containers";
import { TimeRangeFields } from "./TimeRangeFields";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Pulls the audio track out of a video file and saves it as a standalone MP3, WAV, M4A, OGG or FLAC file (or copies the original audio untouched), useful for turning a recorded video into a podcast-style audio file or just keeping the sound without the visuals. Optionally extract just a specific time range instead of the whole video. Runs entirely in your browser — the video never leaves your device.`,
  how: `Upload your video, choose a Save as format, and click Extract Audio. A video with no sound track is reported as soon as it loads. To take the sound from several videos, choose Several videos (ZIP): they are saved with the same format and come back as one ZIP. Check "Extract a specific time range" if you only want part of it — drag the range slider or type exact start/end times. Download the result when it finishes; MP3 is the default. Its default setting is variable-quality, so speech often comes out near 95 kbps; pick MP3 128 kbps or 192 kbps for a fixed bitrate.`,
  tip: `The first time you use any audio or video tool on this site, your browser downloads a small processing engine (about 25MB) — your browser may keep it so later runs start faster, but it can be downloaded again. Some phone-recorded videos (particularly .mov files) use a codec this tool can't read — if processing fails right away, try re-exporting the video from your phone or camera app first.`,
};

const stamp = (t: number) => `${Math.floor(t / 60)}m${String(Math.floor(t % 60)).padStart(2, "0")}s`;

const FORMAT_OPTIONS = (
  <>
    <option value="mp3">MP3 (variable quality, smaller for speech)</option><option value="mp3-128">MP3 128 kbps</option><option value="mp3-192">MP3 192 kbps</option><option value="wav">WAV (uncompressed)</option><option value="m4a">M4A (AAC)</option><option value="ogg">OGG</option><option value="flac">FLAC</option>
    <option value="original">Original audio (no re-encode, instant)</option>
  </>
);

export default function ExtractAudio() {
  const { intermediate } = useAudioPipelineStep();
  const [mode, setMode] = useState<"one" | "many">("one");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [useRange, setUseRange] = useState(false);
  // Inside a bundle, copy the original audio so the first step does not add a lossy encode of its own.
  const [fmt, setFmt] = useState(intermediate ? "original" : "mp3");
  const [resultExt, setResultExt] = useState("mp3");
  const [range, setRange] = useState<[number, number]>([0, 10]);
  const [rangeValid, setRangeValid] = useState(true);
  const [noAudio, setNoAudio] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    if (f.type.startsWith("audio/") || /\.(mp3|wav|flac|ogg|m4a|aac|opus)$/i.test(f.name)) {
      toast.error("That is already an audio file. Use Audio Converter or Audio Trimmer for audio, or drop a video here.");
      return;
    }
    if (f.type && !f.type.startsWith("video/")) {
      toast.error("That doesn't look like a video file. Drop an MP4, MOV, WebM, MKV or similar.");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    setDuration(0);
    setUseRange(false);
    setRange([0, 10]);
    setRangeValid(true);
    setNoAudio(false);
    // Looked up in the file's own header, so a silent video says so the moment it loads (MP4, MOV, WebM, MKV; other
    // containers are found out when Extract Audio runs, with the same message).
    videoHasAudio(f).then((has) => { if (has === false) setNoAudio(true); });
  }, []);

  const onLoadedMetadata = () => {
    const d = videoRef.current?.duration;
    if (d && Number.isFinite(d)) {
      setDuration(d);
      setRange([0, d]); // whole file by default; a fixed 10 s silently dropped the rest
    }
  };

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const extractAudio = async () => {
    if (!file || noAudio) return;
    if (useRange && range[1] <= range[0]) {
      toast.error("End time must be after start time");
      return;
    }
    if (!/^(video|audio)\//.test(file.type) && !/\.(mp4|m4v|mov|webm|mkv|avi|wmv|flv|3gp|mpg|mpeg|ogv|ts|mts|m2ts|mp3|m4a|wav|ogg|opus|flac|aac)$/i.test(file.name)) {
      toast.error("This doesn't look like a video file. Choose a video (MP4, MOV, WebM, MKV...) to take the sound from.");
      return;
    }
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading video file...");
      const bytes = await fetchFile(file);

      setStatusMessage(useRange ? "Extracting audio from selected range..." : "Extracting audio...");
      const r = await extractAudioFromVideo(ffmpeg, bytes, fmt, useRange ? range : null, setProgress, controller.signal);
      if (r.fellBack) toast.info("This audio can't be copied as a plain file, so it is saved as MP3 instead.");
      setResultExt(r.ext);
      setResult(ffmpegOutputToBlob(r.data, r.mime));
      toast.success("Audio extracted!");
    } catch (err) {
      if (err instanceof NoAudioTrackError) {
        setNoAudio(true); // stays on the page, unlike a toast
      } else if (err instanceof Error && /does not contain any stream|matches no streams|no audio/i.test(err.message)) {
        setNoAudio(true);
      } else {
        showAudioError(err);
      }
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  /** One video of a batch: a silent one fails alone with the same message, the rest are extracted. */
  const extractOne = async (f: File, signal?: AbortSignal) => {
    if ((await videoHasAudio(f)) === false) throw new NoAudioTrackError();
    const ffmpeg = await loadFFmpeg();
    try {
      const r = await extractAudioFromVideo(ffmpeg, await fetchFile(f), fmt, null, () => {}, signal);
      return { blob: ffmpegOutputToBlob(r.data, r.mime), fileName: `${f.name.replace(/\.[^.]+$/, "")}.${r.ext}` };
    } finally {
      resetFFmpegEngine(ffmpeg);
    }
  };

  const formatPicker = (
    <div className="flex items-center gap-2 text-sm">
      <Label>Save as</Label>
      <select className="border rounded px-2 py-1 bg-background" value={fmt} onChange={(e) => { setFmt(e.target.value); setResult(null); }} aria-label="Output format">{FORMAT_OPTIONS}</select>
    </div>
  );

  const modeToggle = (
    <div className="flex items-center gap-2 mb-4" role="radiogroup" aria-label="How many videos">
      {([["one", "One video"], ["many", "Several videos (ZIP)"]] as const).map(([m, label]) => (
        <Button key={m} size="sm" variant={mode === m ? "default" : "outline"} role="radio" aria-checked={mode === m} disabled={processing} onClick={() => setMode(m)}>{label}</Button>
      ))}
    </div>
  );

  if (mode === "many") {
    return (
      <ToolContainer>
        {modeToggle}
        <BatchRunner accept="video/*" noun="video" process={extractOne} zipName="extracted-audio.zip" runLabel="Extract all" maxSizeMb={500}>
          {formatPicker}
        </BatchRunner>
        <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
      </ToolContainer>
    );
  }

  return (
    <ToolContainer>
      {!file && modeToggle}
      {!file ? (
        <FileDropZone gateAtDownload accept="video/*" maxSize={500} onFiles={handleFiles} label="Drop a video to extract audio" description="Extract audio track from video files" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}

          <video ref={videoRef} src={previewUrl} controls onLoadedMetadata={onLoadedMetadata} className="w-full max-h-64 rounded-lg border" />

          {noAudio && (
            <div role="alert" data-testid="no-audio-track" className="p-3 rounded-lg border border-destructive/50 text-sm text-destructive flex items-center justify-between gap-3">
              <span>{NO_AUDIO_TRACK_MESSAGE} There is nothing to extract. Nothing was used from your trial.</span>
              <Button size="sm" variant="outline" onClick={() => { setFile(null); setNoAudio(false); setDuration(0); }}>Choose another</Button>
            </div>
          )}

          {duration > 0 && !noAudio && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="use-range" checked={useRange} onCheckedChange={(v) => { setUseRange(!!v); setResult(null); }} />
                <Label htmlFor="use-range" className="text-sm font-normal cursor-pointer">Extract a specific time range instead of the whole video</Label>
              </div>

              {useRange && (
                <div className="space-y-2 pt-1">
                  <Slider
                    aria-label="Time range to extract"
                    value={range}
                    onValueChange={(v) => { setRange([v[0], v[1]]); setResult(null); }}
                    min={0}
                    max={duration}
                    step={0.1}
                    minStepsBetweenThumbs={1}
                  />
                  <TimeRangeFields range={range} duration={duration} onChange={(r) => { setRange(r); setResult(null); }} onValidChange={setRangeValid} />
                  <div className="flex justify-between text-xs text-muted-foreground font-mono">
                    <span>{formatMinSec(range[0])}</span>
                    <span>Total: {formatMinSec(duration)}</span>
                    <span>{formatMinSec(range[1])}</span>
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Extracting {formatMinSec(range[1] - range[0])} of {formatMinSec(duration)}
                  </p>
                </div>
              )}
            </div>
          )}

          {!noAudio && formatPicker}
          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && <ResultPreview blob={result} />}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={`${file.name.replace(/\.[^.]+$/, "")}${useRange ? `_${stamp(range[0])}-${stamp(range[1])}` : ""}.${resultExt}`} label={`Download ${resultExt.toUpperCase()}`} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={extractAudio} disabled={processing || noAudio || sizeCheck?.severity !== "ok" || (useRange && !rangeValid)}>{processing ? "Extracting..." : "Extract Audio"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
